import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, User, Phone, Mail, Dog, LogOut, Plus, 
  CheckCircle, XCircle, AlertCircle, Trash2, Edit, Eye,
  ChevronDown, ChevronUp, Filter, RefreshCw, PawPrint
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('adminToken');

  // Vérifier l'authentification
  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/appointments?token=${token}`),
        axios.get(`${API}/admin/stats?token=${token}`)
      ]);
      setAppointments(appointmentsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}?token=${token}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${id}?token=${token}`);
      toast.success('Rendez-vous supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateNotes = async (id, notes) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}?token=${token}`, { notes });
      toast.success('Notes mises à jour');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      completed: { label: 'Terminé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </span>
    );
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    return apt.status === filter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Non précisée';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#8B9E8A] to-[#9CAF88] text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PawPrint className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Gratia Animalis</h1>
                <p className="text-white/80 text-sm">Espace Administrateur</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="text-white/80 hover:text-white text-sm">
                Voir le site →
              </a>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="border-[#C5A572]/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#8B9E8A]">{stats.total || 0}</p>
              <p className="text-sm text-gray-600">Total RDV</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{stats.pending || 0}</p>
              <p className="text-sm text-yellow-600">En attente</p>
            </CardContent>
          </Card>
          <Card className="border-blue-300 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.confirmed || 0}</p>
              <p className="text-sm text-blue-600">Confirmés</p>
            </CardContent>
          </Card>
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{stats.completed || 0}</p>
              <p className="text-sm text-green-600">Terminés</p>
            </CardContent>
          </Card>
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{stats.cancelled || 0}</p>
              <p className="text-sm text-red-600">Annulés</p>
            </CardContent>
          </Card>
          <Card className="border-[#C5A572]/30 bg-[#C5A572]/10">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#B8860B]">{stats.thisMonth || 0}</p>
              <p className="text-sm text-[#B8860B]">Ce mois</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:border-[#C5A572] focus:outline-none"
            >
              <option value="all">Tous les RDV</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmés</option>
              <option value="completed">Terminés</option>
              <option value="cancelled">Annulés</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-[#8B9E8A] text-[#8B9E8A] hover:bg-[#8B9E8A]/10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white hover:shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un RDV
            </Button>
          </div>
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-[#8B9E8A] mx-auto mb-4" />
            <p className="text-gray-600">Chargement...</p>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <Card className="border-[#C5A572]/30">
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucun rendez-vous trouvé</p>
              <p className="text-gray-400 text-sm mt-2">
                {filter !== 'all' ? 'Essayez de changer le filtre' : 'Les demandes de RDV apparaîtront ici'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAppointments.map((apt) => (
              <Card key={apt.id} className="border-[#C5A572]/30 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Info principale */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{apt.animalType === 'cheval' ? '🐴' : '🐕'}</span>
                        <h3 className="text-xl font-bold text-[#8B9E8A]">{apt.name}</h3>
                        {apt.isManual && (
                          <Badge variant="outline" className="text-xs">Manuel</Badge>
                        )}
                        {getStatusBadge(apt.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-[#C5A572]" />
                          <a href={`tel:${apt.phone}`} className="hover:text-[#C5A572]">{apt.phone}</a>
                        </div>
                        {apt.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-[#C5A572]" />
                            <a href={`mailto:${apt.email}`} className="hover:text-[#C5A572]">{apt.email}</a>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[#C5A572]" />
                          <span>Souhaité: {formatDate(apt.preferredDate)}</span>
                        </div>
                      </div>

                      {apt.message && (
                        <p className="mt-3 text-gray-700 bg-gray-50 p-3 rounded-lg text-sm">
                          💬 {apt.message}
                        </p>
                      )}

                      {apt.notes && (
                        <p className="mt-2 text-[#8B9E8A] bg-[#8B9E8A]/10 p-3 rounded-lg text-sm">
                          📝 Notes: {apt.notes}
                        </p>
                      )}

                      <p className="text-xs text-gray-400 mt-3">
                        Reçu le {formatDate(apt.createdAt)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 min-w-[180px]">
                      <select
                        value={apt.status}
                        onChange={(e) => handleStatusChange(apt.id, e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-[#C5A572] focus:outline-none"
                      >
                        <option value="pending">⏳ En attente</option>
                        <option value="confirmed">✅ Confirmé</option>
                        <option value="completed">✔️ Terminé</option>
                        <option value="cancelled">❌ Annulé</option>
                      </select>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowDetailModal(apt)}
                          className="flex-1 text-[#8B9E8A] border-[#8B9E8A]"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(apt.id)}
                          className="text-red-500 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Modal Ajouter RDV */}
      {showAddModal && (
        <AddAppointmentModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
          token={token}
        />
      )}

      {/* Modal Détail/Edit */}
      {showDetailModal && (
        <DetailModal
          appointment={showDetailModal}
          onClose={() => setShowDetailModal(null)}
          onUpdate={(notes) => handleUpdateNotes(showDetailModal.id, notes)}
          token={token}
        />
      )}
    </div>
  );
};

// Modal pour ajouter un RDV
const AddAppointmentModal = ({ onClose, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    animalType: 'cheval',
    preferredDate: '',
    confirmedDate: '',
    message: '',
    notes: '',
    status: 'confirmed'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/appointments/manual?token=${token}`, formData);
      toast.success('Rendez-vous ajouté avec succès');
      onSuccess();
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-[#8B9E8A] to-[#9CAF88] text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Ajouter un rendez-vous
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Nom du client *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Nom complet"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Téléphone *</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                placeholder="+33 6 XX XX XX XX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@exemple.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Type d'animal *</label>
              <select
                value={formData.animalType}
                onChange={(e) => setFormData({...formData, animalType: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="cheval">🐴 Cheval</option>
                <option value="chien">🐕 Chien</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Date du RDV</label>
              <Input
                type="date"
                value={formData.confirmedDate}
                onChange={(e) => setFormData({...formData, confirmedDate: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmé</option>
                <option value="completed">Terminé</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#8B9E8A] mb-1">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Notes internes..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white"
              >
                {loading ? 'Ajout...' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Modal de détail
const DetailModal = ({ appointment, onClose, onUpdate }) => {
  const [notes, setNotes] = useState(appointment.notes || '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="bg-gradient-to-r from-[#8B9E8A] to-[#9CAF88] text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Détails du rendez-vous
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Client</p>
              <p className="font-semibold">{appointment.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Animal</p>
              <p className="font-semibold">{appointment.animalType === 'cheval' ? '🐴 Cheval' : '🐕 Chien'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <a href={`tel:${appointment.phone}`} className="font-semibold text-[#C5A572]">{appointment.phone}</a>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-semibold">{appointment.email || 'Non renseigné'}</p>
            </div>
          </div>

          {appointment.message && (
            <div>
              <p className="text-sm text-gray-500">Message</p>
              <p className="bg-gray-50 p-3 rounded-lg mt-1">{appointment.message}</p>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-500 mb-1">Notes internes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajouter des notes..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Fermer
            </Button>
            <Button
              onClick={() => onUpdate(notes)}
              className="flex-1 bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white"
            >
              Sauvegarder
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
